"use client";

import { Coins, Info } from "@repo/ui/components/icons";
import { cn } from "@repo/ui/lib/utils";
import { useSearchParams } from "next/navigation";
import { useRef } from "react";

import type { BuybackEstimate, Metal } from "../../../lib/api-client";
import { formatTomanFromRial, METAL_FA } from "../../../lib/product-display";
import { BarThumb, Choice, Progress, StepButtons, unique } from "./WizardParts";

const STEPS = ["طلا/نقره؟", "عیار و وزن", "برآورد قیمت"] as const;
type Step = 1 | 2 | 3;

const estimateLabel = (rial: string | null) => (rial === null ? "قیمت خرید در دسترس نیست" : formatTomanFromRial(rial));

/** "X تا Y" across bars, or one amount when they agree. */
function range(list: BuybackEstimate[]) {
  const amounts = list.flatMap((p) => (p.buybackRial === null ? [] : [BigInt(p.buybackRial)]));
  if (!amounts.length) return null;
  const min = amounts.reduce((a, b) => (b < a ? b : a));
  const max = amounts.reduce((a, b) => (b > a ? b : a));
  return min === max ? formatTomanFromRial(min.toString()) : `${formatTomanFromRial(min.toString())} تا ${formatTomanFromRial(max.toString())}`;
}

/**
 * /buy «فروش» (F125): a public estimate of what ShemShop pays for a bar it
 * sold, from today's buy price (F80): metal → carat and weight → the matching
 * bars with their estimate. Choices live in the URL like the buy wizard's
 * (/buy?mode=sell&metal=gold&fineness=24k&weight=1g&step=3). Selling itself
 * (sign-in, the user's own bars, a request) is the panel flow of 2.16.
 */
export function SellWizard({ enabled, products }: { enabled: boolean; products: BuybackEstimate[] }) {
  const params = useSearchParams();
  const topRef = useRef<HTMLDivElement>(null);

  if (!enabled) {
    return (
      <div className="bg-background flex flex-col items-center gap-2 rounded-card p-8 text-center shadow-sm">
        <Info size={28} className="text-muted-foreground" />
        <p className="font-semibold leading-7">امکان فروش در این لحظه میسر نیست، لطفا بعدا مراجعه کنید.</p>
      </div>
    );
  }

  const metals = (["gold", "silver"] as const).filter((m) => products.some((p) => p.metal === m));
  const metal = metals.find((m) => m === params.get("metal"));
  const forMetal = products.filter((p) => p.metal === metal);
  const finenesses = unique(
    forMetal.map((p) => p.fineness),
    (f) => f.slug,
  );
  const fineness = finenesses.length === 1 ? finenesses[0] : finenesses.find((f) => f.slug === params.get("fineness"));
  const forFineness = forMetal.filter((p) => p.fineness.slug === fineness?.slug);
  const weights = unique(
    forFineness.map((p) => p.weight),
    (w) => w.slug,
  ).sort((a, b) => Number(a.grams) - Number(b.grams));
  const weight = weights.find((w) => w.slug === params.get("weight"));
  const bars = forFineness.filter((p) => p.weight.slug === weight?.slug);

  const reachable: Step = !metal ? 1 : !fineness || !weight ? 2 : 3;
  const asked = Number(params.get("step") ?? 1);
  const step = Math.min(asked >= 1 && asked <= 3 ? asked : 1, reachable) as Step;

  function go(patch: Record<string, string | null>, nextStep: Step) {
    const next = new URLSearchParams(params);
    next.set("mode", "sell");
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    if (nextStep > 1) next.set("step", String(nextStep));
    else next.delete("step");
    window.history.pushState(null, "", `/buy?${next}`);
    if (nextStep !== step && topRef.current && topRef.current.getBoundingClientRect().top < 0) {
      topRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }
  const goStep = (s: Step) => go({}, s);

  if (products.length === 0) {
    return (
      <div className="bg-background rounded-card p-8 text-center shadow-sm">
        <p className="font-semibold">برآورد قیمت فروش در حال حاضر در دسترس نیست.</p>
      </div>
    );
  }

  const canNext = step < reachable;

  return (
    <div ref={topRef} className="flex scroll-mt-6 flex-col gap-4 pb-24 sm:pb-0">
      <Progress steps={STEPS} label="مراحل فروش" step={step} reachable={reachable} onPick={goStep} />

      <section className="bg-background rounded-card p-4 shadow-sm sm:p-6" aria-labelledby="sell-step-title">
        <h2 id="sell-step-title" className="mb-4 font-bold">
          {step === 1 ? "چه شمشی دارید؟" : step === 2 ? "عیار و وزن شمش شما" : "برآورد قیمت خرید شمش شما"}
        </h2>

        {step === 1 ? (
          <div className="grid grid-cols-2 gap-3">
            {(["gold", "silver"] as const).map((m: Metal) => {
              const own = products.filter((p) => p.metal === m);
              return (
                <Choice
                  key={m}
                  selected={metal === m}
                  disabled={own.length === 0}
                  onClick={() => go({ metal: m, fineness: null, weight: null }, 1)}
                  className="items-center py-6 text-center"
                >
                  <span className={cn("flex size-14 items-center justify-center rounded-full", m === "gold" ? "bg-tint-yellow text-ink-yellow" : "bg-muted text-muted-foreground")}>
                    <Coins size={28} />
                  </span>
                  <span className="mt-2 text-base font-bold">شمش {METAL_FA[m]}</span>
                </Choice>
              );
            })}
          </div>
        ) : null}

        {step === 2 && metal ? (
          <div className="flex flex-col gap-5">
            <div>
              <p className="text-muted-foreground mb-2 text-sm">عیار</p>
              <div className="flex flex-wrap gap-2">
                {finenesses.map((f) => (
                  <button
                    key={f.slug}
                    type="button"
                    onClick={() => {
                      const keeps = forMetal.some((p) => p.fineness.slug === f.slug && p.weight.slug === weight?.slug);
                      go({ fineness: f.slug, weight: keeps ? weight!.slug : null }, 2);
                    }}
                    aria-pressed={fineness?.slug === f.slug}
                    className={cn("rounded-full px-4 py-2 text-sm font-medium transition-colors", fineness?.slug === f.slug ? "bg-primary text-primary-foreground" : "bg-surface hover:bg-muted")}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-muted-foreground mb-2 text-sm">وزن</p>
              {fineness ? (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
                  {weights.map((w) => (
                    <Choice key={w.slug} selected={weight?.slug === w.slug} onClick={() => go({ weight: w.slug }, 2)}>
                      <span className="font-bold">{w.label}</span>
                      <span className="text-muted-foreground text-xs">{range(forFineness.filter((p) => p.weight.slug === w.slug)) ?? "قیمت خرید در دسترس نیست"}</span>
                    </Choice>
                  ))}
                </div>
              ) : (
                <p className="bg-surface text-muted-foreground rounded-2xl p-4 text-sm">اول عیار را انتخاب کنید.</p>
              )}
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="flex flex-col gap-4">
            <ul className="grid gap-3 sm:grid-cols-2">
              {bars.map((p) => (
                <li key={p.id} className="bg-surface flex items-center gap-3 rounded-2xl p-2.5">
                  <BarThumb product={p} />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="line-clamp-2 text-sm leading-6 font-semibold">{p.title}</span>
                    <span className="text-muted-foreground text-xs">
                      {p.brand.name} · {p.packaging.name}
                    </span>
                    <span className={cn("mt-1 text-sm font-bold", p.buybackRial === null && "text-muted-foreground text-xs font-medium")}>
                      {estimateLabel(p.buybackRial)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="bg-tint-yellow text-ink-yellow rounded-2xl p-3 text-xs leading-6">
              این مبلغ تقریبی و بر پایه قیمت لحظه‌ای خرید است. مبلغ نهایی پس از تحویل شمش به نماینده شمشاپ و بررسی آن اعلام می‌شود. فقط شمش‌هایی که از
              شمشاپ خریده‌اید قابل فروش است.
            </p>
            <p className="bg-surface text-muted-foreground rounded-2xl p-3 text-sm leading-7">
              برای فروش، پس از ورود به حساب، شمش‌هایی را که از شمشاپ خریده‌اید انتخاب می‌کنید. ثبت درخواست فروش به‌زودی در پنل کاربری فعال می‌شود.
            </p>
          </div>
        ) : null}

        <div className="mt-6 hidden items-center justify-between gap-3 sm:flex">
          <StepButtons step={step} last={3} canNext={canNext} onBack={() => goStep((step - 1) as Step)} onNext={() => goStep((step + 1) as Step)} />
        </div>
      </section>

      {/* Phones: back and next stay in reach, just above the tab bar. */}
      <div className="bg-background/95 fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+5.25rem)] z-20 mx-3 flex items-center justify-between gap-3 rounded-header px-3 py-2.5 shadow-md backdrop-blur-sm sm:hidden">
        <StepButtons step={step} last={3} canNext={canNext} onBack={() => goStep((step - 1) as Step)} onNext={() => goStep((step + 1) as Step)} />
      </div>
    </div>
  );
}
